import React, { useMemo } from "react";
import { Typography, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { IReportDetailRow } from "./report-detail-types";
import { getTemplateValue } from "../reporting/reporting-helpers";
import StatusChip from 'components/status-chip';
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import GetAppIcon from "@material-ui/icons/GetApp";
import MailOutlineIcon from "@material-ui/icons/MailOutline";

// Shared with the report grid's LinkCell; kept re-exported here for existing imports.
export { getDownloadFileNameFromUrl, downloadResource } from "helpers/download-helpers";
import { downloadResource } from "helpers/download-helpers";

const buildStyles   = makeStyles(theme => ({
  value: {
    fontSize: 16,
    fontWeight: 500,
  },
  linkWrapper: {
    display: "inline-flex",
  },
  linkButton: {
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "none",
  },
}));

interface IValueProps {
  data: any;
  row: IReportDetailRow;
}

const RowValue : React.FC<IValueProps> = ({row, data}) => {
  switch(row.type){
    case "link": return <LinkValue row={row} data={data} />;
    case "status": return <StatusValue row={row} data={data} />;
    case "text":
    default:
      return <TextValue row={row} data={data} />;
  }
}

export default RowValue;

//===
// Value component for the Row.type of "text" (or empty - this is the default)
const TextValue : React.FC<IValueProps> = ({row, data}) => {
  const classes = buildStyles();
  const value = useMemo(() => getTemplateValue(row.value, data, ""), [row, data]);

  return (
    <Typography className={classes.value}>{value}</Typography>
  );
}

//===
// Value component for the Row.type of "link"
const LinkValue : React.FC<IValueProps> = ({row, data}) => {
  const classes = buildStyles();
  const { link } = row;
  const value = useMemo(() => getTemplateValue(row.value, data, ""), [row, data]);
  const prefix = useMemo(() => link?.type === "mailto" ? "mailto:" : "", [link]);
  const resolvedUrl = useMemo(() => link?.url ? getTemplateValue(link.url, data, "") : value, [link, data, value]);
  const resolvedText = useMemo(() => link?.text ? getTemplateValue(link.text, data, "") : undefined, [link, data]);
  const buttonLabel = useMemo(() => {
    if(resolvedText && resolvedText.trim().length > 0) return resolvedText;
    if(link?.type === "download") return "Download";
    if(link?.type === "mailto") return "Email";
    return "Open";
  }, [resolvedText, link]);
  const buttonColor: "primary" | "secondary" = link?.type === "download" ? "secondary" : "primary";
  const isDownload = link?.type === "download";
  const resolvedHref = resolvedUrl ? `${prefix}${resolvedUrl}` : "";
  const icon = useMemo(() => {
    if(link?.type === "download") return <GetAppIcon fontSize="small" />;
    if(link?.type === "mailto") return <MailOutlineIcon fontSize="small" />;
    return <OpenInNewIcon fontSize="small" />;
  }, [link]);
  
  //If no link properties, then just display a text value
  if(!link) return <TextValue row={row} data={data} />;  

  const onDownloadClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isDownload || !resolvedUrl) {
      return;
    }

    event.preventDefault();
    try {
      await downloadResource(resolvedHref);
    } catch {
      window.open(resolvedHref, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Typography className={classes.value} component="span">
      {resolvedUrl ? (
        <span className={classes.linkWrapper}>
          <Button
            variant="contained"
            color={buttonColor as any}
            className={classes.linkButton}
            href={resolvedHref}
            target={link.target || "_blank"}
            rel="noreferrer"
            onClick={isDownload ? onDownloadClick : undefined}
            endIcon={icon}
          >
            {buttonLabel}
          </Button>
        </span>
      ) : null}
    </Typography>
  )
};

//===
// Value component for the Row.type of "status"
const StatusValue : React.FC<IValueProps> = ({row, data}) => {
  const value = useMemo(() => getTemplateValue(row.value, data, ""), [row, data]);
  
  return (
    <StatusChip status={value}/>
  );
}

